import { deletePushSubscription, getPushKey, savePushSubscription } from '../api/portal-service';

/**
 * Browser notifications for the portal.
 *
 * **Nothing here is a second notification system.** A subscription is a delivery address for the
 * rows the panel already reads: the same table, the same types, the same parameters. If none of
 * this runs — an old browser, an iPhone that was never added to the home screen, a deployment with
 * no key pair — the portal is exactly as it was, which is the property that makes it safe to add.
 *
 * **Permission is asked for on a tap and never on a load.** A prompt that appears because somebody
 * opened a page is the one every browser penalises and every person refuses, and a refusal is
 * permanent for the origin: no button of ours can ask again.
 */

/** Written for the service worker, which cannot read `localStorage`. */
const PREFERENCES_CACHE = 'ipodariger-preferences';

const LANGUAGE_REQUEST = '/preferences/language';

export interface PushState {
    /** Whether this browser can do it at all. */
    supported: boolean;

    /** Whether this deployment has a key pair to subscribe against. */
    available: boolean;

    subscribed: boolean;

    /** Permission was refused. The browser will not ask again. */
    blocked: boolean;
}

export function pushSupported(): boolean {
    return (
        typeof window !== 'undefined' &&
        typeof navigator !== 'undefined' &&
        'serviceWorker' in navigator &&
        'PushManager' in window &&
        'Notification' in window &&
        window.isSecureContext
    );
}

/**
 * Where this browser stands, and — when it is already subscribed — the heartbeat that keeps the row
 * addressable: a push service may rotate an endpoint at any time and the row is keyed on it.
 */
export async function readPushState(signal?: AbortSignal): Promise<PushState> {
    if (!pushSupported()) {
        return { supported: false, available: false, subscribed: false, blocked: false };
    }

    const blocked = Notification.permission === 'denied';

    let publicKey: string | null = null;

    try {
        publicKey = (await getPushKey(signal)).publicKey;
    } catch {
        return { supported: true, available: false, subscribed: false, blocked };
    }

    if (publicKey === null) {
        return { supported: true, available: false, subscribed: false, blocked };
    }

    try {
        // Registering asks for nothing: it is the permission prompt people object to, not the
        // worker, and without it an already-granted subscription cannot be read.
        const registration = await register();
        const subscription = await registration.pushManager.getSubscription();

        if (subscription === null) {
            return { supported: true, available: true, subscribed: false, blocked };
        }

        await save(subscription);

        return { supported: true, available: true, subscribed: true, blocked: false };
    } catch {
        return { supported: true, available: true, subscribed: false, blocked };
    }
}

/** Asks for permission, subscribes, and tells the server. Called from a tap and nowhere else. */
export async function enablePush(): Promise<PushState> {
    const state = await readPushState();

    if (!state.available || state.subscribed) {
        return state;
    }

    const permission = await Notification.requestPermission();

    if (permission !== 'granted') {
        return { ...state, blocked: permission === 'denied' };
    }

    try {
        const { publicKey } = await getPushKey();

        if (publicKey === null) {
            return { ...state, available: false };
        }

        const registration = await register();

        const subscription =
            (await registration.pushManager.getSubscription()) ??
            (await registration.pushManager.subscribe({
                // Required by every browser: a push that shows nothing could wake a device
                // silently, and none of them will issue one.
                userVisibleOnly: true,
                applicationServerKey: decodeKey(publicKey)
            }));

        await save(subscription);

        return { ...state, subscribed: true, blocked: false };
    } catch {
        return { ...state, subscribed: false };
    }
}

/** Stops delivery to this browser: the server first, then the browser. */
export async function disablePush(): Promise<PushState> {
    const state = await readPushState();

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();

        if (subscription) {
            // The row first. A browser that unsubscribed while the row survived leaves the server
            // pushing at an endpoint that answers 410 until something deletes it.
            await deletePushSubscription(subscription.endpoint);
            await subscription.unsubscribe();
        }
    } catch {
        // Nothing to recover: the switch stays where the state below puts it.
    }

    return { ...state, subscribed: false };
}

/**
 * Signing out. The row goes; the browser permission stays, because it belongs to the person rather
 * than to the session, and asking for it again tomorrow is the prompt this design avoids.
 */
export async function forgetPushSubscription(): Promise<void> {
    if (!pushSupported()) {
        return;
    }

    try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();

        if (subscription) {
            await deletePushSubscription(subscription.endpoint);
            await subscription.unsubscribe();
        }
    } catch {
        // Signing out must not fail because a browser would not let go of a subscription.
    }
}

/** Mirrors the language the portal is being read in where the service worker can read it. */
export async function rememberPushLanguage(language: string): Promise<void> {
    if (typeof caches === 'undefined') {
        return;
    }

    try {
        const cache = await caches.open(PREFERENCES_CACHE);
        await cache.put(LANGUAGE_REQUEST, new Response(language));
    } catch {
        // Without it the worker renders in Kazakh, which is a language this product is in.
    }
}

function register(): Promise<ServiceWorkerRegistration> {
    return navigator.serviceWorker.register('/sw.js', { type: 'module' });
}

async function save(subscription: PushSubscription): Promise<void> {
    const keys = subscription.toJSON().keys;

    if (!keys?.p256dh || !keys?.auth) {
        return;
    }

    await savePushSubscription({
        endpoint: subscription.endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth
    });
}

/** The application server key travels as base64url and is subscribed with as bytes. */
function decodeKey(base64Url: string): ArrayBuffer {
    const padded = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const binary = atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='));
    const bytes = new Uint8Array(new ArrayBuffer(binary.length));

    for (let index = 0; index < binary.length; index += 1) {
        bytes[index] = binary.charCodeAt(index);
    }

    return bytes.buffer;
}
